import type { SavingsPointsData } from '../hooks/useGetSavingsPoints';

const POINTS_PER_FAMILY = 300;

export type TopupSuggestion = {
  amount: number;
  monthsSaved: number;
};

export function getTopupSuggestion(data: SavingsPointsData): TopupSuggestion | null {
  const additionalDp = parseFloat(data.additional_downpayment_needed);
  if (additionalDp <= 0) return null;

  const atCurrent = Math.ceil(parseFloat(data.additional_months_at_current));
  const atDownpmt = Math.ceil(parseFloat(data.additional_months_at_downpayment));
  const monthsSaved = atCurrent - atDownpmt;
  if (monthsSaved <= 0) return null;

  return {
    amount: (parseFloat(data.pfhpp_investment) || 0) + additionalDp,
    monthsSaved,
  };
}

export function formatTopupLabel(monthsSaved: number): string {
  if (monthsSaved >= 12) {
    const years = Math.round(monthsSaved / 12);
    return `${years} yr${years !== 1 ? 's' : ''} earlier`;
  }
  return `${monthsSaved} month${monthsSaved !== 1 ? 's' : ''} earlier`;
}

export function formatPoints(value: string | null | undefined): string {
  if (!value) return '0';
  return Math.round(parseFloat(value)).toLocaleString();
}

export function formatCurrency(value: string | null | undefined): string {
  if (!value) return '£0';
  return `£${Math.round(parseFloat(value)).toLocaleString()}`;
}

export function formatCurrencyShort(value: string | null | undefined): string {
  if (!value) return '£0';
  const num = Math.round(parseFloat(value));
  if (num >= 1000 && num % 1000 === 0) return `£${num / 1000}K`;
  if (num >= 1000) return `£${(num / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return `£${num.toLocaleString()}`;
}

export function progressPercent(earned: string, required: string): number {
  const earnedNum = parseFloat(earned);
  const requiredNum = parseFloat(required);
  if (!requiredNum) return 0;
  return Math.min(100, Math.round((earnedNum / requiredNum) * 100));
}

export function downPaymentPercent(investment: string, target: string): number {
  const investmentNum = parseFloat(investment);
  const targetNum = parseFloat(target);
  if (!targetNum) return 0;
  return Math.min(100, Math.round((investmentNum / targetNum) * 100));
}

export function familiesHelped(pointsEarned: string | null | undefined): number {
  if (!pointsEarned) return 0;
  return Math.floor(parseFloat(pointsEarned) / POINTS_PER_FAMILY);
}

export function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function parseContactMonth(contactMonth: string): Date {
  const [year = 0, month = 1] = contactMonth.split('-').map(Number);
  return new Date(year, month - 1, 1);
}

export function formatContactMonth(contactMonth: string): string {
  return parseContactMonth(contactMonth).toLocaleDateString('en-GB', {
    month: 'short',
    year: 'numeric',
  });
}
